import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Address } from '@mfe/shared/schema-types';

import {
  ScrubAddressProcessStatus,
  ScrubAddressRecommendation,
} from '../../../../platform/src/server/dataSources/PAL/types';
import { EditableCardType, ScrubAddressModalType } from './types';
import { AddressR0 } from '@viasat/res-apps-lib/build/types/vail';

export type ScrubbedAddressState = {
  modalType: ScrubAddressModalType | null;
  editingCard: EditableCardType;
  closeAfterReload: boolean;
  loading: boolean;
  rawInput: Address;
  scrubbedAddress: {
    rawAddress: AddressR0;
    formattedAddress: string | null;
    processStatus?: ScrubAddressProcessStatus;
    recommendation?: ScrubAddressRecommendation;
  };
};

export const initialScrubbedAddressState: ScrubbedAddressState = {
  modalType: null,
  editingCard: EditableCardType.None,
  closeAfterReload: false,
  loading: false,
  rawInput: {
    addressLines: [''],
    countryCode: '',
    municipality: '',
    postalCode: '',
    region: '',
  },
  scrubbedAddress: {
    rawAddress: {
      addressLines: [''],
      countryCode: '',
      municipality: '',
      postalCode: '',
      region: '',
      country: '',
    },
    formattedAddress: '',
  },
};

export const scrubbedAddressSlice = createSlice({
  name: 'scrubbedAddress',
  initialState: initialScrubbedAddressState,
  reducers: {
    setEditingCard: (state, action: PayloadAction<EditableCardType>) => {
      state.editingCard = action.payload;
    },
    setCloseAfterReload: (state, action: PayloadAction<boolean>) => {
      state.closeAfterReload = action.payload;
    },
    setShowScrubModal: (state, action: PayloadAction<ScrubAddressModalType | null>) => {
      state.modalType = action.payload;
    },
    setScrubbedAddress: (
      state,
      {
        payload,
      }: {
        payload: Omit<
          ScrubbedAddressState,
          'loading' | 'modalType' | 'editingCard' | 'editedAddress' | 'closeAfterReload'
        >;
      }
    ) => {
      state.loading = false;
      state.rawInput = payload.rawInput;
      state.scrubbedAddress = payload.scrubbedAddress;
    },
    fetchScrubbedAddress: (state, action: PayloadAction<Address>) => {
      state.loading = true;
    },
    setScrubbedAddressLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    resetScrubbedAddress: (state) => {
      return {
        ...state,
        rawInput: initialScrubbedAddressState.rawInput,
        scrubbedAddress: initialScrubbedAddressState.scrubbedAddress,
      };
    },
  },
});

export const {
  setShowScrubModal,
  setEditingCard,
  setCloseAfterReload,
  setScrubbedAddress,
  fetchScrubbedAddress,
  setScrubbedAddressLoading,
  resetScrubbedAddress,
} = scrubbedAddressSlice.actions;

export const selectScrubbedAddress = (state: { scrubbedAddress: ScrubbedAddressState }) => state.scrubbedAddress;
